import { ApprovedVendorAllocation, CalculatorType, InverterType, VendorEquipmentTypes } from 'types';
import type { AvlAllocationDetail, FeocDataItem } from 'types';

import { ApprovedVendorAllocationLabels } from './allocationLabels';

export type FieldKind =
  | 'text'
  | 'textarea'
  | 'number'
  | 'checkbox'
  | 'select'
  | 'multiselect'
  | 'size'
  | 'aliases'
  | 'feocData'
  | 'section';

export interface FieldSpec<TName extends string = string> {
  name: TName;
  label: string;
  kind: FieldKind;
  required?: boolean;
  options?: { value: string; label: string }[];
  helperText?: string;
}

export const emptyValueLabel = '--';

/**
 * Read-only value formatting shared by the detail view. Returns a discriminated
 * shape per FieldKind so the view layer can render text vs. tags vs. vintage rows
 * without re-deriving which fields belong to an equipment type. This keeps the
 * detail page and the edit form driven by the same registry — the view can never
 * drift from the field set.
 */
export type FormattedFieldValue =
  | { display: 'text'; value: string }
  | { display: 'tags'; values: string[] }
  | { display: 'aliases'; values: { source: string; value: string }[] }
  | { display: 'feocData'; rows: FeocDataItem[] };

export interface AvlItemLike {
  [key: string]:
    | string
    | number
    | boolean
    | string[]
    | { source: string; value: string }[]
    | { value: number; unit: string }
    | FeocDataItem[]
    | undefined
    | null;
}

const formatSimple = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return emptyValueLabel;
  return String(value);
};

/** Bulk edit can set the value without the unit; omit the missing half. */
const formatSize = (size: { value?: number | null; unit?: string | null } | null | undefined): string => {
  if (size === null || size === undefined || size.value === undefined || size.value === null) {
    return emptyValueLabel;
  }
  return size.unit ? `${size.value} ${size.unit}` : String(size.value);
};

/** Sorts a copy so a badge's values read alphabetically regardless of source order. */
const sortDisplayValues = (values: unknown[]): string[] =>
  values.map(String).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

/**
 * Format a single registry field for read-only display against an AVL item.
 * `labelFor` resolves option/enum codes to their human labels (e.g. program types).
 */
export const formatFieldValue = (
  spec: FieldSpec,
  item: AvlItemLike,
  labelFor?: (fieldName: string, code: string) => string,
): FormattedFieldValue => {
  const raw = item[spec.name];

  switch (spec.kind) {
    case 'multiselect': {
      const codes = Array.isArray(raw) ? (raw as string[]) : [];
      return { display: 'tags', values: codes.map((code) => labelFor?.(spec.name, code) ?? code) };
    }
    case 'aliases': {
      const aliases = Array.isArray(raw) ? (raw as { source: string; value: string }[]) : [];
      return { display: 'aliases', values: aliases };
    }
    case 'feocData': {
      const rows = Array.isArray(raw) ? (raw as FeocDataItem[]) : [];
      return { display: 'feocData', rows };
    }
    case 'checkbox':
      return { display: 'text', value: raw ? 'Yes' : 'No' };
    case 'size': {
      const size = raw as { value?: number; unit?: string } | undefined | null;
      return { display: 'text', value: formatSize(size) };
    }
    case 'select': {
      const code = formatSimple(raw);
      if (code === emptyValueLabel) return { display: 'text', value: emptyValueLabel };
      return { display: 'text', value: labelFor?.(spec.name, code) ?? code };
    }
    default:
      return { display: 'text', value: formatSimple(raw) };
  }
};

const calculatorTypeOptions = Object.values(CalculatorType).map((v) => ({ value: v, label: v }));
const inverterTypeOptions = Object.values(InverterType).map((v) => ({ value: v, label: v }));
const allocationOptions = Object.values(ApprovedVendorAllocation).map((v) => ({ value: v, label: v }));

/**
 * Base fields rendered for every equipment type once one is selected.
 * Identity + classification + shared operational fields.
 *
 * Note: `manufacturer` is omitted here — it is rendered separately in the form header
 * via PalmettoAutocomplete (with type-scoped suggestions) before this list.
 */
export const baseFields: FieldSpec[] = [
  { name: 'modelNumber', label: 'Model Number', kind: 'text', required: true },
  { name: 'name', label: 'Name', kind: 'text', required: true },
  { name: 'sku', label: 'SKU', kind: 'text' },
  {
    name: 'allocation',
    label: 'Allocation',
    kind: 'select',
    required: true,
    options: allocationOptions,
  },
  {
    name: 'programTypes',
    label: 'Program Types',
    kind: 'multiselect',
    helperText: 'Select which Program Types this item belongs to',
  },
  { name: 'needsApproval', label: 'Needs Approval', kind: 'checkbox' },
  { name: 'aliases', label: 'Aliases', kind: 'aliases' },
  { name: 'description', label: 'Description', kind: 'textarea' },
];

/**
 * Per-equipment-type spec fields, exhaustive over VendorEquipmentTypes.
 * These are appended AFTER baseFields when a type is selected.
 *
 * Fields here must match what importAvlFromCsv.ts accepts per type — inverter, panel, storage,
 * and mounting each have their own schema/mappers there; the remaining HVAC types have no CSV
 * import support and get base fields only.
 */
export const equipmentFields: Record<VendorEquipmentTypes, FieldSpec[]> = {
  [VendorEquipmentTypes.inverter]: [
    { name: 'inverterType', label: 'Inverter Type', kind: 'select', options: inverterTypeOptions },
    { name: 'maxACkW', label: 'Max AC kW', kind: 'number' },
    { name: 'maxDCkW', label: 'Max DC kW', kind: 'number' },
    { name: 'domesticContentPercentageMlpe', label: 'DC % MLPE', kind: 'number' },
    { name: 'domesticContentPercentageString', label: 'DC % String', kind: 'number' },
    { name: 'feocPercentageMlpe', label: 'FEOC % MLPE', kind: 'number' },
    { name: 'feocPercentageString', label: 'FEOC % String', kind: 'number' },
    { name: 'requiresExtendedWarranty', label: 'Requires Extended Warranty', kind: 'checkbox' },
    { name: 'extendedWarrantyCost', label: 'Extended Warranty Cost', kind: 'number' },
    { name: 'omnidianAlias', label: 'Omnidian Alias', kind: 'text' },
    { name: 'calculatorType', label: 'Calculator Type', kind: 'select', options: calculatorTypeOptions },
    { name: 'feocData', label: 'Safe Harbor Vintage Data', kind: 'feocData' },
  ],
  [VendorEquipmentTypes.panel]: [
    { name: 'size', label: 'Wattage', kind: 'size' },
    { name: 'domesticContentPercentageMlpe', label: 'DC % MLPE', kind: 'number' },
    { name: 'domesticContentPercentageString', label: 'DC % String', kind: 'number' },
    { name: 'feocPercentageMlpe', label: 'FEOC % MLPE', kind: 'number' },
    { name: 'feocPercentageString', label: 'FEOC % String', kind: 'number' },
    { name: 'omnidianAlias', label: 'Omnidian Alias', kind: 'text' },
    { name: 'calculatorType', label: 'Calculator Type', kind: 'select', options: calculatorTypeOptions },
  ],
  [VendorEquipmentTypes.storage]: [
    { name: 'section:capacity', label: 'Capacity', kind: 'section' },
    { name: 'size', label: 'Listed Capacity kWH', kind: 'size' },
    { name: 'gridPairedACkW', label: 'Grid Paired AC kW', kind: 'number' },
    { name: 'gridPairedPeakACkW', label: 'Grid Paired Peak AC kW', kind: 'number' },
    { name: 'usableCapacityKWh', label: 'Usable Capacity kWH', kind: 'number' },
    { name: 'section:features', label: 'Features', kind: 'section' },
    { name: 'arbitrage', label: 'Supports Arbitrage', kind: 'checkbox' },
    { name: 'backup', label: 'Supports Backup', kind: 'checkbox' },
    { name: 'section:compliance', label: 'Compliance', kind: 'section' },
    { name: 'bessDomesticContentPercentage', label: 'BESS Domestic Content %', kind: 'number' },
    { name: 'bessFeocPercentage', label: 'BESS FEOC %', kind: 'number' },
    { name: 'section:pricing', label: 'Pricing', kind: 'section' },
    { name: 'maxPrice', label: 'Max Price', kind: 'number' },
    { name: 'section:warranty', label: 'Warranty', kind: 'section' },
    { name: 'requiresExtendedWarranty', label: 'Requires Extended Warranty', kind: 'checkbox' },
    { name: 'extendedWarrantyCost', label: 'Extended Warranty Cost', kind: 'number' },
    { name: 'section:other', label: 'Other', kind: 'section' },
    { name: 'omnidianAlias', label: 'Omnidian Alias', kind: 'text' },
    { name: 'calculatorType', label: 'Calculator Type', kind: 'select', options: calculatorTypeOptions },
    { name: 'feocData', label: 'Safe Harbor Vintage Data', kind: 'feocData' },
  ],
  [VendorEquipmentTypes.mounting]: [
    { name: 'domesticContentSupported', label: 'Domestic Content Supported', kind: 'checkbox' },
    { name: 'domesticContentPercentageMlpe', label: 'DC % MLPE', kind: 'number' },
    { name: 'domesticContentPercentageString', label: 'DC % String', kind: 'number' },
    { name: 'feocPercentageMlpe', label: 'FEOC % MLPE', kind: 'number' },
    { name: 'feocPercentageString', label: 'FEOC % String', kind: 'number' },
    { name: 'category', label: 'Category', kind: 'text' },
    { name: 'calculatorType', label: 'Calculator Type', kind: 'select', options: calculatorTypeOptions },
  ],
  [VendorEquipmentTypes.heatPump]: [],
  [VendorEquipmentTypes.airHandler]: [],
  [VendorEquipmentTypes.heatStrip]: [],
  [VendorEquipmentTypes.thermostat]: [],
  [VendorEquipmentTypes.furnace]: [],
  [VendorEquipmentTypes.waterHeater]: [],
};

/** First-seen spec per field name across base + all equipment specs (labels are consistent per name). */
const specByName: Record<string, FieldSpec> = (() => {
  const map: Record<string, FieldSpec> = {};
  for (const spec of [...baseFields, ...Object.values(equipmentFields).flat()]) {
    if (!map[spec.name]) map[spec.name] = spec;
  }
  return map;
})();

/**
 * Human labels for every registry field plus the header identity fields not in the registry
 * (manufacturer/equipmentType). Used by the change-history view; falls back to the raw key.
 */
export const fieldLabels: Record<string, string> = {
  manufacturer: 'Manufacturer',
  equipmentType: 'Equipment Type',
  allocationDetails: 'Allocation Schedule',
  ...Object.fromEntries(Object.values(specByName).map((spec) => [spec.name, spec.label])),
  // `size` is "Wattage" for panels but "Capacity" for storage; history has no equipment-type
  // context, so use a neutral label to avoid showing a misleading one.
  size: 'Size',
};

export const fieldLabel = (fieldName: string): string => fieldLabels[fieldName] ?? fieldName;

/**
 * Format an arbitrary change-history value using the same rules as the read-only view, keyed by
 * the field's registry kind. Handles aliases ({source,value}[]), size ({value,unit}), feocData
 * (vintage summary), booleans (Yes/No), string arrays (sorted, joined), and scalars; empty/absent → `--`.
 */
export const formatHistoryValue = (fieldName: string, value: unknown): string => {
  if (value === undefined || value === null || value === '') return emptyValueLabel;

  if (fieldName === 'allocationDetails') {
    const rows = Array.isArray(value) ? (value as AvlAllocationDetail[]) : [];
    const active = rows.filter((row) => !row.archived);
    if (active.length === 0) return emptyValueLabel;
    // Append-only + strictly-increasing startDate: the max-startDate row is the one this change added.
    const latest = active.reduce((a, b) => (b.startDate > a.startDate ? b : a));
    const label = ApprovedVendorAllocationLabels[latest.allocationStatus] ?? latest.allocationStatus;
    return `Added window starting ${latest.startDate}: ${label}`;
  }

  const kind = specByName[fieldName]?.kind;

  if (
    kind === 'aliases' ||
    (Array.isArray(value) && value.every((v) => v && typeof v === 'object' && 'value' in v))
  ) {
    const aliases = value as { source?: string; value: string }[];
    if (aliases.length === 0) return emptyValueLabel;
    return aliases.map((a) => (a.source ? `${a.source}: ${a.value}` : a.value)).join(', ');
  }

  if (
    kind === 'feocData' ||
    (Array.isArray(value) && value.every((v) => v && typeof v === 'object' && 'vintage' in v))
  ) {
    const rows = value as { vintage: string }[];
    if (rows.length === 0) return emptyValueLabel;
    return `${rows.length} vintage ${rows.length === 1 ? 'row' : 'rows'}`;
  }

  if (kind === 'size' || (value && typeof value === 'object' && 'unit' in value && 'value' in value)) {
    return formatSize(value as { value?: number; unit?: string });
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value)) return value.length ? sortDisplayValues(value).join(', ') : emptyValueLabel;

  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};
